import { useLanguage } from '../i18n/LanguageContext';
import { Reveal, Overline } from './motion';
import { PanelSketch } from './PanelSketch';

const WOOD = (a, b, c) => `repeating-linear-gradient(92deg, ${a} 0px, ${b} 7px, ${c} 14px, ${b} 21px)`;

// Panel textures (visual approximation, CSS only)
export const TEXTURES = [
  { key: 'smooth', bg: '#EFEEEA', border: true },
  { key: 'woodgrain', bg: WOOD('#C9CDD0', '#E8EAEB', '#D4D8DA'), border: true },
  { key: 'stucco', bg: 'radial-gradient(circle at 30% 40%, #D8DBDD 0%, #C4C8CA 45%, #D0D3D5 100%)' },
  { key: 'ultratouch', bg: WOOD('#7A4E20', '#C89B5F', '#8A5A28') },
  { key: 'ultrasmooth', bg: '#F7F7F5', border: true },
  { key: 'perfecttouch', bg: WOOD('#6B4423', '#B98D5E', '#7D5330') },
];

export const RAL = [
  { name: 'Alb trafic', ral: 'RAL 9002', bg: '#F5F5F2', hex: '#F5F5F2', border: true },
  { name: 'Alb satin', ral: 'RAL 9002', bg: 'linear-gradient(135deg, #F5F5F2 0%, #E4E4DE 100%)', hex: '#EEEEE9', stops: [[0, '#F5F5F2'], [1, '#E4E4DE']], border: true },
  { name: 'Antracit', ral: 'RAL 7016', bg: '#383E42', hex: '#383E42' },
  { name: 'Grafit negru', ral: 'RAL 7021', bg: '#2C3237', hex: '#2C3237' },
  { name: 'Argintiu', ral: 'RAL 9006', bg: 'linear-gradient(135deg, #B9BCBE 0%, #9EA2A5 100%)', hex: '#AEB2B5', stops: [[0, '#B9BCBE'], [1, '#9EA2A5']] },
  { name: 'Maro', ral: 'RAL 8019', bg: '#45322E', hex: '#45322E' },
  { name: 'Verde deschis', ral: 'RAL 6005', bg: '#12402F', hex: '#12402F' },
  { name: 'Verde închis', ral: 'RAL 6009', bg: '#2A3826', hex: '#2A3826' },
  { name: 'Albastru', ral: 'RAL 5010', bg: '#0F4C81', hex: '#0F4C81' },
  { name: 'Roșu', ral: 'RAL 3000', bg: '#A72920', hex: '#A72920' },
];

export const WOOD_SPECIAL = [
  { name: 'Stejar auriu', bg: WOOD('#7A4E20', '#C89B5F', '#8A5A28'), hex: '#B98D5E', stops: [[0, '#7A4E20'], [0.35, '#C89B5F'], [0.7, '#8A5A28'], [1, '#C89B5F']], stock: true },
  { name: 'Stejar deschis', bg: WOOD('#A67B45', '#D9BC8A', '#B3895A'), hex: '#C9A870', stops: [[0, '#A67B45'], [0.35, '#D9BC8A'], [0.7, '#B3895A'], [1, '#D9BC8A']] },
  { name: 'Nuc', bg: WOOD('#33200F', '#5C4033', '#3B2419'), hex: '#4A2F1C', stops: [[0, '#33200F'], [0.35, '#5C4033'], [0.7, '#3B2419'], [1, '#5C4033']], stock: true },
  { name: 'Mahon', bg: WOOD('#401511', '#6B2B22', '#4C1A15'), hex: '#54201A', stops: [[0, '#401511'], [0.35, '#6B2B22'], [0.7, '#4C1A15'], [1, '#6B2B22']], stock: true },
  { name: 'Wenge', bg: WOOD('#1E1510', '#3A2B22', '#261B14'), hex: '#2A1F18', stops: [[0, '#1E1510'], [0.35, '#3A2B22'], [0.7, '#261B14'], [1, '#3A2B22']], stock: true },
  { name: 'Concrete Like', bg: 'linear-gradient(135deg, #B4B6B7 0%, #9A9D9E 50%, #ACAEAF 100%)', hex: '#A6A9AA', stops: [[0, '#B4B6B7'], [0.5, '#9A9D9E'], [1, '#ACAEAF']] },
  { name: 'Rusty', bg: 'linear-gradient(135deg, #7C4A2D 0%, #A3613A 40%, #6B3E25 100%)', hex: '#8F5636', stops: [[0, '#7C4A2D'], [0.4, '#A3613A'], [1, '#6B3E25']] },
];

const PANEL_MODELS = [
  { key: 'single', name: 'SINGLE' },
  { key: 'multi', name: 'MULTI' },
  { key: 'flat', name: 'FLAT' },
  { key: 'blocks', name: 'BLOCKS' },
  { key: 'multirib', name: 'MULTI RIB' },
];

const FamilyTitle = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-smoke mb-5">{children}</p>
);

export const GarageFinishes = () => {
  const { t } = useLanguage();
  const g = t('garageFinishes');

  return (
    <section data-testid="garage-finishes-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
      <Reveal className="mb-12 md:mb-16">
        <Overline className="mb-4">{g.overline}</Overline>
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{g.title}</h2>
        <p className="text-smoke max-w-2xl leading-relaxed">{g.desc}</p>
      </Reveal>

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
        <Reveal className="lg:col-span-2">
          <div
            data-testid="panel-models-card"
            className="rounded-[2rem] bg-[#F7F5F1] border border-line/15 p-6 md:p-8 shadow-[0_24px_60px_-24px_rgba(24,26,32,0.28)]"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A8D92] mb-6">{g.modelsTitle}</p>
            <div className="divide-y divide-black/[0.06]">
              {PANEL_MODELS.map((m) => (
                <div key={m.key} data-testid={`panel-model-${m.key}`} className="group flex items-center gap-5 py-4 first:pt-0 last:pb-0">
                  <div className="w-24 h-20 shrink-0 transition-transform duration-500 group-hover:scale-[1.06]">
                    <PanelSketch model={m.key} />
                  </div>
                  <p className="font-display font-medium text-[#1B1D22] tracking-tight">{m.name}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-3 space-y-10 md:space-y-12">
          <Reveal>
            <FamilyTitle>{g.texturesTitle}</FamilyTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
              {TEXTURES.map((tx, i) => (
                <div key={tx.key} data-testid={`garage-texture-${tx.key}`} className="group">
                  <div
                    className={`h-16 md:h-20 rounded-xl transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(24,26,32,0.15)] ${tx.border ? 'border border-line/15' : ''}`}
                    style={{ background: tx.bg }}
                  />
                  <p className="font-display font-medium mt-2.5 text-sm">{g.textureNames[i]}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <FamilyTitle>{g.colorsTitle}</FamilyTitle>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 md:gap-5">
              {RAL.map((c, i) => (
                <div key={c.ral + c.name} data-testid={`garage-ral-${c.ral.replace(' ', '-').toLowerCase()}-${c.name.toLowerCase().replace(/ /g, '-')}`} className="group">
                  <div
                    className={`h-14 md:h-16 rounded-lg transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(24,26,32,0.15)] ${c.border ? 'border border-line/15' : ''}`}
                    style={{ background: c.bg }}
                  />
                  <p className="font-display font-medium mt-2 text-xs md:text-sm leading-tight">{(g.ralNames && g.ralNames[i]) || c.name}</p>
                  <p className="text-smoke text-[10px] md:text-xs">{c.ral}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <FamilyTitle>{g.woodTitle}</FamilyTitle>
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {WOOD_SPECIAL.map((c, i) => (
                <div key={c.name} data-testid={`garage-wood-${c.name.toLowerCase().replace(/ /g, '-')}`} className="group">
                  <div
                    className="relative h-16 md:h-20 rounded-xl transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(24,26,32,0.15)]"
                    style={{ background: c.bg }}
                  >
                    {c.stock && (
                      <span className="absolute top-2 right-2 bg-accent text-white text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full shadow">
                        {g.stock}
                      </span>
                    )}
                  </div>
                  <p className="font-display font-medium mt-2.5 text-sm">{(g.woodNames && g.woodNames[i]) || c.name}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal className="mt-12 md:mt-16">
        <p data-testid="garage-finishes-note" className="text-smoke text-sm border-l-2 border-accent pl-4">{g.note}</p>
      </Reveal>
    </section>
  );
};
